import { useEffect, useState } from "react";
import { Link } from "wouter";
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { paths, REFERRAL_STATUS_LABEL, type Customer, type Referral } from "@clevis/shared-types";
import { Button, Card, CardTitle } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { call } from "../lib/callFunction";
import { useAccess } from "../hooks/useAccess";

// Customer page → Referrals (Clevis Relay): this customer's own link (copy
// it, text it to them), who sent them, and the friends they've sent.

interface MyLink {
  enabled: boolean;
  eligible: boolean;
  code: string;
  link: string;
  shareText: string;
  reward: string;
}
const money = (c: number) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export function ReferralCard({ tenantId, customerId, customer }: { tenantId: string; customerId: string; customer: Customer }) {
  const can = useAccess();
  const [mine, setMine] = useState<MyLink | null>(null);
  const [sent, setSent] = useState<Array<Referral & { id: string }>>([]);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    call<MyLink>("getMyReferralLink", { tenantId, customerId }, { mutation: false })
      .then(setMine)
      .catch(() => setMine({ enabled: false, eligible: false, code: "", link: "", shareText: "", reward: "" }));
  }, [tenantId, customerId]);
  useEffect(() => {
    if (!can("referrals")) return;
    return onSnapshot(query(collection(db, paths.tenantReferrals(tenantId)), where("referrerCustomerId", "==", customerId), orderBy("createdAt", "desc"), limit(50)), (snap) =>
      setSent(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Referral) }))),
    );
  }, [tenantId, customerId, can]);

  if (!mine) return null;
  if (!mine.enabled && !customer.referredBy && sent.length === 0) return null;

  return (
    <Card className="space-y-2">
      <CardTitle className="mb-0">Referrals</CardTitle>
      {customer.referredBy ? (
        <p className="text-xs text-steel">
          Sent to you by{" "}
          <Link href={`/customers/${customer.referredBy.customerId}`} className="text-ink hover:underline">
            {customer.referredBy.name || "a customer"}
          </Link>
          .
        </p>
      ) : null}
      {mine.enabled && !mine.eligible ? <p className="text-xs text-steel">No referral link: the program is for homeowners (this customer has a company name).</p> : null}
      {mine.eligible ? (
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <code className="rounded-md bg-cream px-2 py-1 text-xs text-ink">{mine.link}</code>
            <button
              type="button"
              className="text-xs font-semibold text-brandorange-dark hover:text-brandorange"
              onClick={() => {
                void navigator.clipboard.writeText(mine.link);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
            >
              {copied ? "copied ✓" : "copy"}
            </button>
            {customer.phone ? (
              <Button
                type="button"
                variant="ghost"
                className="px-2 py-1 text-xs"
                busy={busy}
                onClick={async () => {
                  setBusy(true);
                  setNote("");
                  try {
                    const r = await call<{ ok: boolean; reason: string }>("textReferralLink", { tenantId, customerId });
                    setNote(r.ok ? "Texted." : r.reason || "Couldn't text it.");
                  } catch (e) {
                    setNote((e as Error).message);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Text it to them
              </Button>
            ) : null}
            {note ? <span className="text-xs text-steel">{note}</span> : null}
          </div>
          <p className="text-[11px] text-steel">They get {mine.reward} when a friend's first job is paid.</p>
        </div>
      ) : null}
      {sent.length ? (
        <div className="divide-y divide-steel/10 text-xs">
          {sent.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-2 py-1">
              <span className="min-w-0 truncate text-ink">
                {r.customerId ? (
                  <Link href={`/customers/${r.customerId}`} className="hover:underline">
                    {r.friendName || "A friend"}
                  </Link>
                ) : (
                  r.friendName || "A friend"
                )}
              </span>
              <span className="shrink-0 text-steel">
                {REFERRAL_STATUS_LABEL[r.status]}
                {r.status === "paid" && r.rewardCents ? ` · ${money(r.rewardCents)}` : ""}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </Card>
  );
}
